import {
    Box,
    CircularProgress,
    Grid,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { ArrowBack } from '@mui/icons-material';
import { t } from 'i18next';
import { fetchUser } from '../../../../services/users';
import { SelectionButton } from '../../../../component/buttons';
import RecordInfo from './RecordInfo';
import { MapTracking } from './MapTracking';
import HrChart from './charts/HrChart';
import GroupedHrZoneCards from './charts/GroupedHrZoneCards';
import HrTimeline from './charts/HrTimeline';
import EcgViewer from './ECGViewer';
import { MainCard } from 'component/cards/MainCard2';
import { useRecordANN, useRecordHr } from 'hooks/useRecords';

const Record = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { record, from, seeUserState } = location.state || {};
    const [userData, setUserData] = useState(null);
    const roleUuid = record?.patient.role_uuid || '';
    const user_data = userData?.data?.user_data ?? null;
    const [selectedTab, setSelectedTab] = useState(0);
    const [openHrDialog, setOpenHrDialog] = useState(false);

    const [ecgTimeWindow, setEcgTimeWindow] = useState(null);
    const deviceName = record?.config?.name || 'Sem identificacao';
    const deviceId = record?.config?.id || 'Nao disponivel';

    const TYPE_LABELS = {
        rehabilitation: t('exercise.types.rehabilitation'),
        treadmill: t('exercise.types.treadmill'),
        ergometer: t('exercise.types.ergometer'),
        walking: t('exercise.types.walking'),
        running: t('exercise.types.running'),
        cycling: t('exercise.types.cycling'),
        rest: t('exercise.types.rest'),
        easy: t('exercise.types.easy'),
        other: t('exercise.types.other'),
        squat: t('exercise.types.squat'),
        sit_to_stand: t('exercise.types.sit_to_stand'),
        push: t('exercise.types.push'),
        pull: t('exercise.types.pull'),
        arm_raise: t('exercise.types.arm_raise'),
        knee_extension: t('exercise.types.knee_extension')
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [userResp] = await Promise.all([fetchUser(roleUuid)]);
                setUserData(userResp);
            } catch (error) {
                console.error('Erro ao procurar dados do paciente:', error);
            }
        };
        fetchData();
    }, [roleUuid]);

    const REQUIRED_HR_FIELDS = [
        'maxHeartRate',
        'trainingHeartRate',
        'minTrainingHeartRate',
        'restingHeartRate',
        'anaerobicThreshold',
        'ventilatoryThreshold'
    ];

    const hasHrThresholds = useMemo(() => {
        if (!user_data) return false;

        return REQUIRED_HR_FIELDS.every((field) => {
            const value = user_data[field];
            return value !== null && value !== undefined && value !== '';
        });
    }, [user_data]);

    useEffect(() => {
        if (user_data && !hasHrThresholds) {
            setOpenHrDialog(true);
        }
    }, [user_data, hasHrThresholds]);

    const hasEcg = record?.config?.dataTypes?.includes('ECG');
    //const hasEcg = true;

    const tabs = [
        { label: t('records.tabs.hr'), key: 'hr' },
        ...(hasEcg ? [{ label: t('records.tabs.ecg'), key: 'ecg' }] : []),
        { label: t('records.tabs.gps'), key: 'gps' }
    ];

    const canLoadHr = Boolean(record && user_data);

    const { hrPoints, hrMax, hrMin, dataHrZone } = useRecordHr(
        canLoadHr ? record : null,
        canLoadHr ? user_data : null
    );

    const hasAnn = record?.config?.dataTypes?.includes('ANN');
    const canLoadAnn = hasAnn && Boolean(record?.uuid);

    const { data: annData } = useRecordANN(canLoadAnn ? record.uuid : null);

    const annotations = useMemo(() => {
        if (!annData?.ts?.length) return [];

        return annData.ts.map((timestamp, i) => ({
            iso: timestamp,
            time: new Date(timestamp).toLocaleTimeString('pt-PT', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            }),
            label: annData.text[i],
            type: annData.type[i],
            value: annData.value[i]
        }));
    }, [annData]);

    const buildExerciseIntervals = (anns, hrPoints) => {
        if (!anns?.length || !hrPoints?.length) return [];

        const exercises = anns
            .filter((a) => a.type?.includes('E-EXERCISE'))
            .sort((a, b) => new Date(a.iso) - new Date(b.iso));

        if (!exercises.length) return [];

        const intervals = [];
        const firstIso = hrPoints[0]?.iso || record.ti;
        const firstTime = hrPoints[0]?.time;
        const lastIso = hrPoints[hrPoints.length - 1]?.iso || record.tf;
        const lastTime = hrPoints[hrPoints.length - 1]?.time;

        let currentStart = null;
        let currentLabel = null;

        for (const ann of exercises) {
            let state = null;
            try {
                state = ann.value ? JSON.parse(ann.value).state : null;
            } catch {
                state = null;
            }

            if (state === 'start') {
                currentStart = { iso: ann.iso, time: ann.time };

                const translated =
                    TYPE_LABELS[ann.label?.toLowerCase()] || ann.label || 'Exercício';
                currentLabel = translated;
            } else if (state === 'stop') {
                const startIso = currentStart?.iso || firstIso;
                const startTime = currentStart?.time || firstTime;

                const translated =
                    TYPE_LABELS[currentLabel?.toLowerCase()] ||
                    TYPE_LABELS[ann.label?.toLowerCase()] ||
                    currentLabel ||
                    ann.label ||
                    'Exercício';

                intervals.push({
                    label: translated,
                    startIso,
                    startTime,
                    endIso: ann.iso,
                    endTime: ann.time
                });

                currentStart = null;
                currentLabel = null;
            }
        }

        if (currentStart) {
            intervals.push({
                label:
                    TYPE_LABELS[currentLabel?.toLowerCase()] ||
                    currentLabel ||
                    'Exercício',
                startIso: currentStart.iso,
                startTime: currentStart.time,
                endIso: lastIso,
                endTime: lastTime
            });
        }

        return intervals;
    };

    const exerciseIntervals = useMemo(() => {
        if (!annotations.length || !hrPoints?.length) return [];
        return buildExerciseIntervals(annotations, hrPoints);
    }, [annotations, hrPoints]);

    const isLoading = !record || !user_data;

    if (isLoading) {
        return (
            <Box
                sx={{
                    height: '80vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}
            >
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (user_data && !hasHrThresholds) {
        return (
            <>
                <Dialog open={openHrDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>{t('records.hrThresholdsMissingTitle')}</DialogTitle>

                    <DialogContent>
                        <DialogContentText>
                            {t('records.hrThresholdsMissingMessage')}
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() =>
                                navigate('/users/user/edit', {
                                    state: {
                                        user: {
                                            role_uuid: roleUuid
                                        }
                                    }
                                })
                            }
                        >
                            {t('records.defineHrThresholds')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 2 }}>
                <Tooltip title={t('records.back')}>
                    <IconButton
                        color="primary"
                        onClick={() => {
                            if (from) {
                                navigate(from.pathname, {
                                    state: {
                                        ...from.state,
                                        restoredState: seeUserState
                                    }
                                });
                            } else navigate(-1);
                        }}
                    >
                        <ArrowBack />
                    </IconButton>
                </Tooltip>
            </Box>

            <MainCard
                title={t('records.mainTitle')}
                headerAction={
                    <Box sx={{ textAlign: 'right', pt: 0.5 }}>
                        <Box sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                            <strong>Dispositivo:</strong> {deviceName}
                        </Box>
                        <Box sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                            <strong>ID:</strong> {deviceId}
                        </Box>
                    </Box>
                }
            >
                <RecordInfo
                    record={record}
                    maxHr={hrMax}
                    minHr={hrMin}
                    userData={user_data}
                />
            </MainCard>

            <Grid container spacing={2} sx={{ p: 2 }}>
                {tabs.map((tab, index) => (
                    <Grid item key={tab.key} xs={4}>
                        <SelectionButton
                            selected={selectedTab === index}
                            sx={{ width: '100%', py: 2 }}
                            onClick={() => setSelectedTab(index)}
                        >
                            {tab.label}
                        </SelectionButton>
                    </Grid>
                ))}
            </Grid>

            {tabs[selectedTab].key === 'hr' && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                        <HrChart
                            hrPoints={hrPoints}
                            maxHrReg={hrMax}
                            maxHr={user_data.maxHeartRate}
                            basalHr={user_data.restingHeartRate}
                            trainHr={user_data.trainingHeartRate}
                            minTrainHr={user_data.minTrainingHeartRate}
                            exerciseIntervals={exerciseIntervals}
                            onSelectEcgWindow={setEcgTimeWindow}
                            onGoToEcg={() =>
                                setSelectedTab(tabs.findIndex((t) => t.key === 'ecg'))
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <HrTimeline
                            hrPoints={hrPoints}
                            trainingHr={user_data.trainingHeartRate}
                            minTrainHr={user_data.minTrainingHeartRate}
                            summaryZones={dataHrZone?.summaryZones}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <MainCard title={t('records.zonesTitle')}>
                            <GroupedHrZoneCards
                                groupedZones={dataHrZone?.groupedZones || []}
                                classicZones={dataHrZone?.classicZones || []}
                            />
                        </MainCard>
                    </Grid>
                </Grid>
            )}

            {tabs[selectedTab].key === 'ecg' && (
                <MainCard title="ECG">
                    <EcgViewer
                        recordUuid={record.uuid}
                        ecgTimeWindow={ecgTimeWindow}
                        recordStart={record.ti}
                        recordEnd={record.tf}
                    />
                </MainCard>
            )}

            {tabs[selectedTab].key === 'gps' && (
                <MainCard title="GPS" contentSX={{ p: 0 }}>
                    <MapTracking uuid={record.uuid} />
                </MainCard>
            )}
        </>
    );
};

export default Record;
